import { normalizeJobStatus } from './transcriptionStatus';
import { normalizeTranscriptStatus, parseCertificationMeta } from './transcriptCertification';
import { resolveAudioUrl } from './audioSync';
import { resolveLanguageVersions, getReasonLabel } from './regeneration';
import { MAIN_API_BASE_URL } from '../config/api';

/** Court-facing name for every ASR provider (whisper, nllb, etc.). */
export const DISPLAY_ASR_MODEL = 'Snwolley';

export const parseDurationToSeconds = (value) => {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'number') {
    return value > 100000 ? Math.floor(value / 1000) : Math.floor(value);
  }
  if (typeof value === 'string') {
    const t = value.trim();
    if (t === '') return 0;
    if (/^\d+(\.\d+)?$/.test(t)) {
      return parseFloat(t) > 100000
        ? Math.floor(parseFloat(t) / 1000)
        : Math.floor(parseFloat(t));
    }
    if (/^\d{1,2}(:\d{2}){1,2}$/.test(t)) {
      const parts = t.split(':').map((p) => parseInt(p, 10));
      return parts.length === 3
        ? parts[0] * 3600 + parts[1] * 60 + parts[2]
        : parts[0] * 60 + parts[1];
    }
  }
  return 0;
};

export const getRawDuration = (item) => {
  const fields = {
    duration: item?.duration,
    audio_duration: item?.audio_duration,
    length: item?.length,
    duration_seconds: item?.duration_seconds,
    audio_length: item?.audio_length,
    audio_length_seconds: item?.audio_length_seconds,
    metadata_duration: item?.metadata?.duration,
    metadata_duration_seconds: item?.metadata?.duration_seconds,
  };

  return (
    Object.values(fields).find(
      (val) => val !== null && val !== undefined && val !== 0 && val !== '' && val !== '0'
    ) || 0
  );
};

export const formatDuration = (seconds) => {
  if (!seconds) return '0s';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return h > 0 ? `${h}h ${m}m` : m > 0 ? `${m}m` : `${s}s`;
};

export const formatDate = (dateString) => {
  if (!dateString) return '';
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return String(dateString);
  return d.toISOString().split('T')[0];
};

export const formatDateTime = (dateString) => {
  if (!dateString) return '—';
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return String(dateString);
  return d.toLocaleString();
};

export const getCaseReference = (item) =>
  item?.case_reference ||
  item?.caseReference ||
  item?.metadata?.case_reference ||
  item?.docket_number ||
  '';

export const getCaseId = (item) =>
  item?.case_id || item?.caseId || item?.metadata?.case_id || null;

export const parseTranscriptionRecord = (item) => {
  const rawStatus = item?.status || item?.state || 'pending';
  const metadata = item?.metadata || item?.processing_metadata || {};
  const certification = parseCertificationMeta(item);
  const durationSeconds = parseDurationToSeconds(getRawDuration(item));

  const segments =
    item?.segments ||
    metadata?.segments ||
    item?.speaker_segments ||
    null;

  return {
    id: item?.id || item?.transcription_id,
    title:
      item?.title ||
      item?.filename ||
      item?.name ||
      item?.audio_filename ||
      'Untitled Transcription',
    created_at: item?.created_at || item?.date || item?.uploaded_at || item?.created_date,
    duration: durationSeconds,
    duration_seconds: durationSeconds,
    status: rawStatus,
    jobStatus: normalizeJobStatus(rawStatus),
    transcript_status: normalizeTranscriptStatus(item),
    accuracy: parseFloat(item?.accuracy || item?.confidence || 0),
    transcribed_content:
      item?.transcribed_content || item?.content || item?.transcription || item?.text || '',
    language: item?.language || item?.lang || item?.language_code || metadata?.language || '',
    case_reference: getCaseReference(item),
    case_id: getCaseId(item),
    model_version: DISPLAY_ASR_MODEL,
    queued_at:
      item?.queued_at ||
      metadata?.queued_at ||
      item?.created_at ||
      item?.uploaded_at ||
      null,
    completed_at:
      item?.completed_at ||
      metadata?.completed_at ||
      item?.updated_at ||
      null,
    failure_reason:
      item?.failure_reason ||
      item?.error_message ||
      metadata?.failure_reason ||
      metadata?.error ||
      '',
    source_type: item?.source_type || metadata?.source_type || '',
    audio_url: resolveAudioUrl(item, MAIN_API_BASE_URL),
    segments,
    audio_asset_id: item?.audio_asset_id || metadata?.audio_asset_id || null,
    audio_asset: item?.audio_asset || metadata?.audio_asset || null,
    language_versions: resolveLanguageVersions(item),
    parent_transcription_id: item?.parent_transcription_id || null,
    regeneration_reason: item?.regeneration_reason || metadata?.regeneration_reason || '',
    regeneration_reason_label:
      item?.regeneration_reason_label ||
      metadata?.regeneration_reason_label ||
      getReasonLabel(item?.regeneration_reason || metadata?.regeneration_reason),
    regenerated_at: item?.regenerated_at || metadata?.regenerated_at || null,
    regeneration_requested_by:
      item?.regeneration_requested_by || metadata?.regeneration_requested_by || '',
    session_deleted: Boolean(
      item?.session_deleted ||
        item?.audio_asset?.deleted ||
        metadata?.session_deleted
    ),
    ...certification,
  };
};
